/**
 * TabBar — the shared tab strip primitive.
 *
 * Replaces the hand-rolled `<button>`-row tab headers repeated across
 * windows (DiagnosticHub, CombatLogReplayTab, FeatureManager, …). Two
 * variants: `pills` (filled active chip) and `underline` (active tab keeps
 * an accent underline). Optional per-tab icon and badge count.
 */
import React from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  DURATION,
  EASING,
} from '../theme';

export interface TabDescriptor {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Optional count chip rendered after the label (0/undefined hides it). */
  badge?: number;
}

export type TabBarVariant = 'pills' | 'underline';

export interface TabBarProps {
  tabs: TabDescriptor[];
  activeTab: string;
  onTabChange: (id: string) => void;
  variant?: TabBarVariant;
}

const containerBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: SPACE.xs,
};

const underlineContainer: React.CSSProperties = {
  ...containerBase,
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
};

function tabStyle(
  variant: TabBarVariant,
  active: boolean,
  disabled: boolean,
): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE.sm,
    padding: `${SPACE.sm}px ${SPACE.lg}px`,
    fontSize: FONT_SIZE.md,
    fontWeight: active ? FONT_WEIGHT.semibold : FONT_WEIGHT.medium,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    whiteSpace: 'nowrap',
    background: 'transparent',
    color: active ? COLORS.textPrimary : COLORS.textSecondary,
    transition: `background ${DURATION.fast} ${EASING.standard}, color ${DURATION.fast} ${EASING.standard}`,
  };

  if (variant === 'pills') {
    return {
      ...base,
      border: `1px solid ${active ? COLORS.brand : COLORS.borderSubtle}`,
      borderRadius: RADII.md,
      background: active ? COLORS.surfaceRaised : 'transparent',
      color: active ? COLORS.textPrimary : COLORS.textSecondary,
    };
  }

  // underline
  return {
    ...base,
    border: 'none',
    borderBottom: `2px solid ${active ? COLORS.brand : 'transparent'}`,
    borderRadius: 0,
    marginBottom: -1,
  };
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 16,
  height: 16,
  padding: `0 ${SPACE.xs}px`,
  borderRadius: RADII.pill,
  background: COLORS.surfaceSunken,
  color: COLORS.textSecondary,
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.semibold,
  lineHeight: 1,
};

export function TabBar({
  tabs,
  activeTab,
  onTabChange,
  variant = 'pills',
}: TabBarProps): React.ReactElement {
  return (
    <div
      role="tablist"
      style={variant === 'underline' ? underlineContainer : containerBase}
    >
      {tabs.map((tab) => {
        const active = tab.id === activeTab;
        const disabled = tab.disabled ?? false;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onTabChange(tab.id);
            }}
            style={tabStyle(variant, active, disabled)}
          >
            {tab.icon != null && <span aria-hidden>{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.badge != null && tab.badge > 0 && (
              <span style={badgeStyle}>{tab.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
